import { useRef } from 'react';
import { Box, Tooltip } from '@mui/material';

export interface ScrubberMarker {
  offsetMs: number;
  type: string;
  label: string;
}

// Marker colours by activity type (brand teal for clicks, red for rage/errors…).
const MARKER_COLOR: Record<string, string> = {
  click: '#2ec2b3',
  rageClick: '#e11d48',
  deadClick: '#f97316',
  error: '#ef4444',
  navigation: '#22c55e',
  custom: '#f59e0b',
};
const colorFor = (t: string) => MARKER_COLOR[t] ?? '#94a3b8';
const secs = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

/**
 * A video-style scrubber with activity markers on the track — click a marker or
 * the bar to seek, drag the thumb to scrub. Markers show where the visitor
 * clicked, rage-clicked, hit an error, or navigated (like Clarity's timeline).
 */
export function ReplayScrubber({
  durationMs,
  currentTimeMs,
  markers,
  onSeek,
}: {
  durationMs: number;
  currentTimeMs: number;
  markers: ScrubberMarker[];
  onSeek: (ms: number) => void;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const pct = (ms: number) => (durationMs > 0 ? Math.min(100, Math.max(0, (ms / durationMs) * 100)) : 0);

  const seekTo = (clientX: number) => {
    const el = barRef.current;
    if (!el || durationMs <= 0) return;
    const r = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    onSeek(frac * durationMs);
  };

  const onDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    seekTo(e.clientX);
  };
  const onMove = (e: React.PointerEvent) => {
    if (e.buttons === 1) seekTo(e.clientX); // dragging with the primary button
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, py: 1.5 }}>
      {/* Activity markers as small carets above the bar. */}
      {markers.map((m, i) => (
        <Tooltip key={`c${i}`} title={`${m.label} · ${secs(m.offsetMs)}`} arrow>
          <Box
            role="button"
            aria-label={`Jump to ${m.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onSeek(m.offsetMs);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: `${pct(m.offsetMs)}%`,
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: `7px solid ${colorFor(m.type)}`,
              cursor: 'pointer',
              zIndex: 3,
              transition: 'transform .1s',
              '&:hover': { transform: 'translateX(-50%) scale(1.4)' },
            }}
          />
        </Tooltip>
      ))}

      {/* The track. */}
      <Box
        ref={barRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: 'action.hover', cursor: 'pointer', touchAction: 'none' }}
      >
        {/* Played portion. */}
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct(currentTimeMs)}%`, bgcolor: 'primary.main', borderRadius: 3 }} />
        {/* Thin marker lines on the track. */}
        {markers.map((m, i) => (
          <Box
            key={`l${i}`}
            sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(m.offsetMs)}%`, width: 2, transform: 'translateX(-1px)', bgcolor: colorFor(m.type), opacity: 0.85 }}
          />
        ))}
        {/* Thumb. */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${pct(currentTimeMs)}%`,
            transform: 'translate(-50%, -50%)',
            width: 13,
            height: 13,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            border: '2px solid #fff',
            boxShadow: 1,
            zIndex: 2,
          }}
        />
      </Box>
    </Box>
  );
}
